/**
 * Chi vede cosa: UNA mappa, ruolo → voci del menu e pagina d'arrivo.
 *
 * La leggono il menu laterale, la barra in basso del telefono e la guardia delle
 * rotte (`GuardiaRuolo`). È una comodità d'interfaccia, non la protezione: quella
 * sta nel server e nella RLS. Il cruscotto risponde 403 al venditore, fatture e
 * incassi sono chiusi dalle policy, i prezzi li scrive solo il titolare. Se questa
 * mappa sbagliasse, il venditore vedrebbe una pagina vuota, non un dato.
 *
 * Decisioni del 24-09 (call con Aleandro del 23-09):
 *   · il venditore vede Clienti (i suoi), Preventivi (calcolatore e
 *     preventivatore), Contratti. Cruscotto, Fatture e incassi, Impostazioni: no.
 *   · Calendario e Messaggi glieli aveva dati il CRM e restano. Per spegnerli
 *     basta togliere "calendario" o "messaggi" dalla riga del venditore.
 *   · Guide resta a tutti: sono i video su come si usa il gestionale, nessun dato.
 *   · amministrazione: niente Preventivi (la RLS non glieli fa leggere), niente
 *     Messaggi (per RLS legge zero conversazioni), niente Cruscotto (è del
 *     titolare). Arriva su Fatture e incassi, che è il suo lavoro.
 *   · montatore: oggi non ha pagine sue. Calendario e Guide, arriva sul Calendario.
 *
 * Decisione del 30-10 (c35, Listini): l'app dei listini di Aleandro si apre dal menu a titolare, venditore e
 * amministrazione, se la sezione «Listini» è accesa per loro. Il montatore no. Chi vede «Interni» (i prezzi
 * d'acquisto) lo decide il server (il ruolo nel cookie dei listini), non questa mappa.
 *
 * Decisione del 27-10 (Assistenza, call del 5-10): la voce la vedono tutti e quattro.
 * Cosa c'è dentro lo decide la RLS (0039): il venditore le assistenze dei clienti che
 * vede, il montatore quelle assegnate a lui.
 *
 * Decisione del 25-09: la Sicurezza (il proprio secondo fattore) è di ogni persona,
 * non di un ruolo. Sta fuori dal menu laterale, si apre dal menu utente, e la apre
 * chiunque abbia un ruolo che la mappa conosce: il venditore ci arriva senza che
 * gli si aprano le Impostazioni.
 */

/** Le pagine della persona: una per ognuno, nessuna voce nel menu laterale. */
export const PERSONALI = {
  sicurezza: "/sicurezza",
};

/** Dove sta ogni voce. `SidebarNav` prende i percorsi da qui. */
export const PERCORSI = {
  cruscotto: "/cruscotto",
  oggi: "/oggi",
  clienti: "/clienti",
  assistenza: "/assistenza",
  preventivi: "/preventivi",
  calcolatore: "/preventivi/calcolatore",
  preventivatore: "/preventivi/preventivatore",
  contratti: "/contratti",
  "fatture-incassi": "/fatture-incassi",
  calendario: "/calendario",
  messaggi: "/messaggi",
  impostazioni: "/impostazioni",
  guide: "/guide",
  listini: "/listini",
};

/**
 * `voci`: cosa compare nel menu e cosa si può aprire.
 * `arrivo`: dove si atterra dopo il login (deve stare fra le sue voci).
 * `rapide`: le tre voci della barra in basso sul telefono, prima di «Menu» e «Nuovo». Dal 29-10 seguono
 *   la tavola del pacchetto di Claude Design (U-dashboard-390): Calendario entra per tutti.
 *
 * Decisione del 30-10 (c32): il venditore arriva su «Oggi», la sua giornata, al posto dei Preventivi.
 * «Oggi» non è una sezione (le sezioni stanno nel vincolo della 0042): è una pagina che mette insieme
 * dati di altre sezioni e mostra ogni pezzo solo se la sezione che lo legge è accesa per quella persona.
 * Per questo non sta in SEZIONI e non si spegne.
 */
export const ACCESSI = {
  titolare: {
    arrivo: "/cruscotto",
    voci: [
      "cruscotto", "clienti", "assistenza", "preventivi", "calcolatore", "preventivatore", "listini", "contratti",
      "fatture-incassi", "calendario", "messaggi", "impostazioni", "guide",
    ],
    rapide: ["cruscotto", "clienti", "calendario"],
  },
  amministrazione: {
    arrivo: "/fatture-incassi",
    voci: ["clienti", "assistenza", "listini", "contratti", "fatture-incassi", "calendario", "impostazioni", "guide"],
    rapide: ["clienti", "calendario", "fatture-incassi"],
  },
  venditore: {
    arrivo: "/oggi",
    voci: ["oggi", "clienti", "assistenza", "preventivi", "calcolatore", "preventivatore", "listini", "contratti", "calendario", "messaggi", "guide"],
    rapide: ["oggi", "clienti", "calendario"],
  },
  montatore: {
    arrivo: "/calendario",
    voci: ["assistenza", "calendario", "guide"],
    rapide: ["calendario", "assistenza", "guide"],
  },
};

/**
 * Le sezioni che il titolare accende e spegne per ogni persona, da Impostazioni ›
 * Collaboratori e permessi (0042). Il ruolo dà le voci, le sezioni spente le
 * tolgono; sul titolare non contano. La stessa lista sta nel vincolo
 * `profili_sezioni_note` della 0042 e in `backend/collaboratori.py`: qui il menu,
 * là il dato. Guide non è una sezione: nessun dato, la vede chiunque entri.
 */
export const SEZIONI = [
  { id: "cruscotto", label: "Dashboard", voci: ["cruscotto"] },
  { id: "clienti", label: "Clienti", voci: ["clienti"] },
  { id: "calendario", label: "Calendario", voci: ["calendario"] },
  { id: "preventivi", label: "Preventivi", voci: ["preventivi", "calcolatore", "preventivatore"] },
  { id: "contratti", label: "Contratti", voci: ["contratti"] },
  { id: "fatture-incassi", label: "Fatture e incassi", voci: ["fatture-incassi"] },
  { id: "assistenza", label: "Assistenza", voci: ["assistenza"] },
  { id: "messaggi", label: "Messaggi", voci: ["messaggi"] },
  { id: "impostazioni", label: "Impostazioni", voci: ["impostazioni"] },
  { id: "listini", label: "Listini", voci: ["listini"] },
];

/** Le sezioni che il ruolo dà: quelle con almeno una voce nella sua riga di ACCESSI. */
export function sezioniDelRuolo(ruolo) {
  const voci = ACCESSI[ruolo]?.voci || [];
  return SEZIONI.filter((s) => s.voci.some((v) => voci.includes(v))).map((s) => s.id);
}

const PER_LUNGHEZZA = Object.entries(PERCORSI).sort((a, b) => b[1].length - a[1].length);

/** La voce a cui appartiene un indirizzo: `/preventivi/preventivatore/42` → `preventivatore`. */
export function voceDelPercorso(pathname) {
  const p = (pathname || "").replace(/\/+$/, "") || "/";
  const trovata = PER_LUNGHEZZA.find(([, base]) => p === base || p.startsWith(`${base}/`));
  return trovata ? trovata[0] : null;
}

/**
 * Le voci di una persona: quelle del ruolo, meno le sezioni spente (`profili.sezioni_spente`).
 * Un ruolo che la mappa non conosce non vede niente. Sul titolare le spente non contano.
 */
export function vociDi(ruolo, spente) {
  const via = ruolo === "titolare" ? [] : SEZIONI.filter((s) => (spente || []).includes(s.id)).flatMap((s) => s.voci);
  return (ACCESSI[ruolo]?.voci || []).filter((v) => !via.includes(v));
}

/**
 * Dove atterra una persona. Se la sezione d'arrivo del ruolo è spenta, la prima voce
 * che le resta (Guide c'è sempre). `null` se il ruolo non è nella mappa.
 */
export function arrivoDi(ruolo, spente) {
  const arrivo = ACCESSI[ruolo]?.arrivo;
  if (!arrivo) return null;
  const voci = vociDi(ruolo, spente);
  if (voci.includes(voceDelPercorso(arrivo))) return arrivo;
  return voci.length ? PERCORSI[voci[0]] : null;
}

/** Le voci della barra in basso. */
export function rapideDi(ruolo, spente) {
  const voci = vociDi(ruolo, spente);
  return (ACCESSI[ruolo]?.rapide || []).filter((v) => voci.includes(v));
}

/**
 * Se un ruolo può aprire un indirizzo. `/` si apre sempre: è la porta che
 * rimanda all'arrivo del ruolo. Un indirizzo che non appartiene a nessuna voce
 * resta chiuso: ciò che la mappa non nomina non si apre per sbaglio.
 */
export function puoAprire(ruolo, pathname, spente) {
  const p = (pathname || "/").replace(/\/+$/, "");
  if (p === "") return Boolean(arrivoDi(ruolo, spente));
  if (Object.values(PERSONALI).includes(p)) return Boolean(arrivoDi(ruolo, spente));
  const voce = voceDelPercorso(pathname);
  return voce !== null && vociDi(ruolo, spente).includes(voce);
}
